import {
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  Business as BusinessIcon,
  Call as CallIcon,
  Check,
  DeleteOutline,
  Close,
  EditOutlined,
  Email as EmailIcon,
  FileDownloadOutlined,
  FilterListOutlined,
  GridView as GridViewIcon,
  Language as LanguageIcon,
  MoreVert,
  Person as PersonIcon,
  SearchOutlined,
  ViewList as ViewListIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { Delete, Edit } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataGrid from "../../components/ui/CustomeDataGrid";
import Pagination from "../../components/ui/Pagination";
import apiService from "../../service/Api";
import { RootState } from "../../store/Index";
import { IContact } from "../../types/contact";
import { accentForContactType } from "../../constants/crmAccents";
import { isRealSalutation } from "../../constants/salutations";
import CompletenessScore from "../venues/list-venues/CompletenessScore";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { downloadCSV, downloadVCard, vCardFilename } from "./utils/vCard";

const toWhatsAppLink = (phone: string) => {
  // wa.me needs digits only, no +, spaces, or dashes
  const digits = phone.replace(/[^\d]/g, "");
  return `https://wa.me/${digits}`;
};

const getPhoneCountryCode = (phone: string | null): string | null => {
  if (!phone) return null;
  const parsed = parsePhoneNumberFromString(phone);
  return parsed?.country || null; // e.g. "ZW", "ZA"
};

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const getCountryDisplayName = (code: string): string => {
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
};

// Shared shape for raw API contact records, used by both the normal
// paginated fetch and the phone-country-filter raw batch fetch.
type ContactApiItem = {
  id: number;
  salutation?: string | null;
  contact_source?: string | null;
  contact_type?: "company" | "individual";
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  country?: string | null;
  website?: string | null;
  contact_person?: string | null;
  profile_image_file?: string | null;
  company_logo?: string | null;
  industry?: string | null;
  tax_number?: string | null;
  job_title?: string | null;
  date_of_birth?: string | null;
  facebook_profile?: string | null;
  phone_numbers?: string | null;
  instagram_profile?: string | null;
  twitter_profile?: string | null;
  linkedin_profile?: string | null;
  notes?: string | null;
  category_type?: string | null;
  company_id?: string | null;
  company_name?: string | null;
  company_email?: string | null;
  created_at?: string;
  created_by?: number | null;
  updated_at?: string;
  last_updated_by?: number | null;
  contact_image?: string | null;
  completeness_score?: number | null;
};

type CompanyFilterOption = {
  id: number;
  name: string;
  contact_count?: number;
};

// Shared so every card/list view renders the same name, with the
// salutation prefixed only when a real one was set (not the "N/A"
// placeholder the backend stores for contacts without one).
const getContactDisplayName = (contact: IContact): string => {
  if (contact.contact_type === "company") {
    return contact.name || "Unnamed Company";
  }
  const base =
    `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
    contact.name ||
    "Unnamed Individual";
  return isRealSalutation(contact.salutation)
    ? `${contact.salutation} ${base}`
    : base;
};

// Single shared mapping function so raw-fetch and normal-fetch paths
// can never drift apart from each other.
const mapContactApiItem = (contact: ContactApiItem): IContact => ({
  id: contact.id,
  salutation: contact.salutation || null,
  contact_source: contact.contact_source || null,
  contact_type: contact.contact_type || "individual",
  name: contact.name || null,
  first_name: contact.first_name || null,
  last_name: contact.last_name || null,
  email: contact.email || null,
  phone: contact.phone || null,
  address:
    contact.city || contact.country
      ? `${contact.city || ""}, ${contact.country || ""}`
          .trim()
          .replace(/^,|,$/g, "")
      : null,
  city: contact.city || null,
  country: contact.country || null,
  website: contact.website || null,
  contactPerson:
    contact.contact_type === "company"
      ? contact.contact_person || null
      : `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || null,
  contactPersonAvatar: contact.profile_image_file || null,
  companyLogo: contact.company_logo || null,

  industry: contact.industry || null,
  taxNumber: contact.tax_number || null,
  jobTitle: contact.job_title || null,
  dateOfBirth: contact.date_of_birth || null,
  facebook_profile: contact.facebook_profile || null,
  phone_numbers: contact.phone_numbers || null,
  instagram_profile: contact.instagram_profile || null,
  twitter_profile: contact.twitter_profile || null,
  linkedin_profile: contact.linkedin_profile || null,
  notes: contact.notes || null,
  profile_image_file: contact.profile_image_file || null,
  category_type: contact.category_type || null,
  company_id: contact.company_id || null,
  company_name: contact.company_name || null,
  company_email: contact.company_email || null,
  created_at: contact.created_at || new Date().toISOString(),
  created_by: contact.created_by ?? null,
  updated_at: contact.updated_at,
  last_updated_by: contact.last_updated_by,
  type: contact.contact_type || "individual",
  contact_image: contact.contact_image || undefined,
  completeness_score: contact.completeness_score ?? 0,
});

const checkIncompleteContacts = (list: IContact[]) =>
  list.some((c) => (!c.name && (!c.first_name || !c.last_name)) || !c.email);

const ListContacts: React.FC = () => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<
    "all" | "company" | "individual"
  >("all");
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [warning, setWarning] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const [filterPhoneCountry, setFilterPhoneCountry] = useState<string>("all");
  const [filterCompanyId, setFilterCompanyId] = useState<string>("all");
  const [companyFilterOptions, setCompanyFilterOptions] = useState<
    CompanyFilterOption[]
  >([]);
  const [availablePhoneCountries, setAvailablePhoneCountries] = useState<
    string[]
  >([]);
  // Raw (unpaginated) batch of contacts matching the current phone-country
  // filter criteria. Cached so page changes don't trigger a re-fetch —
  // only filter/search/source changes do.
  const [phoneFilterRawContacts, setPhoneFilterRawContacts] = useState<
    IContact[]
  >([]);

  // State for delete confirmation modal
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [contactToDeleteId, setContactToDeleteId] = useState<number | null>(
    null,
  );
  const [contactToDeleteName, setContactToDeleteName] = useState<string | null>(
    null,
  );
  const [selectedContact, setSelectedContact] = useState<{
    id: number | null;
    name: string | null;
  }>({ id: null, name: null });

  // Selected contacts are stored as full records (id -> IContact), not just
  // ids. That's what lets a selection survive page changes, filter changes,
  // and search: you can tick a few contacts, search for more to add to the
  // export batch, and nothing you already picked disappears — because the
  // record itself travels with the selection instead of relying on it still
  // being present in the currently-loaded `contacts` page.
  const [selectedContactsMap, setSelectedContactsMap] = useState<
    Record<number, IContact>
  >({});

  const selectedContactIds = useMemo(
    () => Object.keys(selectedContactsMap).map(Number),
    [selectedContactsMap],
  );

  const selectedContacts = useMemo(
    () => Object.values(selectedContactsMap),
    [selectedContactsMap],
  );

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: "include",
      ids: new Set(selectedContactIds),
    }),
    [selectedContactIds],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // DataGrid only knows about rows on the current page, so when it reports
  // a new selection model we reconcile that against the accumulated map:
  // rows on this page that got unticked are removed, rows on this page that
  // got ticked are added (with their full record so they survive a page
  // change). Selections made earlier on other pages are left untouched.
  const handleSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      const newIds =
        newSelectionModel.type === "include"
          ? (newSelectionModel.ids as Set<number>)
          : new Set(
              contacts
                .map((c) => c.id)
                .filter(
                  (id) => !(newSelectionModel.ids as Set<number>).has(id),
                ),
            );

      setSelectedContactsMap((prev) => {
        const next = { ...prev };
        contacts.forEach((c) => {
          if (!newIds.has(c.id) && next[c.id]) {
            delete next[c.id];
          }
        });
        newIds.forEach((id) => {
          const found = contacts.find((c) => c.id === id);
          if (found) next[id] = found;
        });
        return next;
      });
    },
    [contacts],
  );

  // Shared toggle used by the grid-view card checkbox, the search-result
  // dropdown, and (indirectly, via handleSelectionChange) the list-view
  // DataGrid — so every entry point writes to the same selection state.
  const toggleContactSelection = useCallback((contact: IContact) => {
    setSelectedContactsMap((prev) => {
      const next = { ...prev };
      if (next[contact.id]) {
        delete next[contact.id];
      } else {
        next[contact.id] = contact;
      }
      return next;
    });
  }, []);

  const [sourceTab, setSourceTab] = useState("all");
  const [allSources, setAllSources] = useState<string[]>([]);

  const [sortField, setSortField] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

  const contactSourceTabs = useMemo(() => {
    return ["all", ...allSources];
  }, [allSources]);

  const activeFilterCount = [
    filterType !== "all",
    filterCompanyId !== "all",
    filterPhoneCountry !== "all",
    sourceTab !== "all",
  ].filter(Boolean).length;

  // Define colors from reduxTheme with fallbacks
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const primaryHoverColor = reduxTheme.primary_colors?.[600] || "#6b4e3d";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[100] || "#D6DDEB";
  const backgroundColor = reduxTheme.neutral_colors?.[900] || "#A9B2C3";

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    [],
  );

  useEffect(() => {
    if (!contactSourceTabs.includes(sourceTab)) {
      setSourceTab("all");
    }
  }, [contactSourceTabs, sourceTab]);

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  // Normal server-paginated fetch. Used whenever no phone-country filter
  // is active. When a phone filter IS active, this is skipped entirely —
  // the raw-batch effect below takes over instead.
  const fetchContacts = useCallback(async () => {
    if (filterPhoneCountry !== "all") return;

    setIsLoading(true);
    setError(null);
    setWarning(null);
    try {
      const params: Record<string, string | number | undefined> = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
        sort_by: sortField,
        sort_order: sortOrder,
      };
      if (filterType !== "all") {
        params.contact_type = filterType;
      }
      if (sourceTab !== "all") {
        params.contact_source = sourceTab;
      }
      if (filterCompanyId !== "all") {
        params.company_id = filterCompanyId;
      }

      const result = await apiService.get<{
        count: number;
        results: ContactApiItem[];
        next: string | null;
        previous: string | null;
      }>("/api/contacts/contact/", params);

      const mappedContacts: IContact[] = result.data.results
        .filter((contact) => contact.id)
        .map(mapContactApiItem);

      setContacts(mappedContacts);
      setTotalItems(result.data.count);
      setRowCount(result.data.count);
      if (checkIncompleteContacts(mappedContacts)) {
        setWarning(
          "Some contacts have missing names or emails, which may affect display or search.",
        );
      }
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to load contacts. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    filterType,
    searchTerm,
    sourceTab,
    filterPhoneCountry,
    filterCompanyId,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Reset to page 1 whenever a drawer filter changes, so we don't land on a
  // blank page if the new result set has fewer pages.
  useEffect(() => {
    setCurrentPage(1);
  }, [filterPhoneCountry, filterCompanyId, filterType]);

  // When a phone-country filter is active, fetch a large raw batch ONCE per
  // change of filter/search/type/source (NOT per page change). Page changes
  // are then handled locally by the slicing effect below, with no network
  // call.
  useEffect(() => {
    if (filterPhoneCountry === "all") return;

    let cancelled = false;

    const fetchRawForPhoneFilter = async () => {
      setIsLoading(true);
      setError(null);
      setWarning(null);
      try {
        const params: Record<string, string | number | undefined> = {
          page: 1,
          page_size: 1500,
          search: searchTerm || undefined,
          sort_by: sortField,
          sort_order: sortOrder,
        };
        if (filterType !== "all") {
          params.contact_type = filterType;
        }
        if (sourceTab !== "all") {
          params.contact_source = sourceTab;
        }
        if (filterCompanyId !== "all") {
          params.company_id = filterCompanyId;
        }

        const result = await apiService.get<{
          count: number;
          results: ContactApiItem[];
          next: string | null;
          previous: string | null;
        }>("/api/contacts/contact/", params);

        if (cancelled) return;

        const mapped: IContact[] = result.data.results
          .filter((c) => c.id)
          .map(mapContactApiItem);

        setPhoneFilterRawContacts(mapped);
      } catch {
        if (cancelled) return;
        setError("Failed to load contacts. Please try again.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchRawForPhoneFilter();

    return () => {
      cancelled = true;
    };
  }, [
    filterPhoneCountry,
    filterType,
    sourceTab,
    searchTerm,
    filterCompanyId,
    sortField,
    sortOrder,
  ]);

  // Re-slice the cached raw batch whenever the page changes (or the raw
  // batch itself changes). No network call here — purely local.
  useEffect(() => {
    if (filterPhoneCountry === "all") return;

    const filtered = phoneFilterRawContacts.filter(
      (c) => getPhoneCountryCode(c.phone) === filterPhoneCountry,
    );
    const start = (currentPage - 1) * pageSize;
    const pageSlice = filtered.slice(start, start + pageSize);

    setContacts(pageSlice);
    setTotalItems(filtered.length);
    setRowCount(filtered.length);

    if (checkIncompleteContacts(pageSlice)) {
      setWarning(
        "Some contacts have missing names or emails, which may affect display or search.",
      );
    }
  }, [phoneFilterRawContacts, filterPhoneCountry, currentPage, pageSize]);

  // Populate the source-tab, phone-country, and employer filter options.
  // Runs once on mount; a single merged fetch instead of two separate ones.
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [result, companiesResult] = await Promise.all([
          apiService.get<{
            results: {
              contact_source?: string | null;
              phone?: string | null;
            }[];
          }>("/api/contacts/contact/", { page_size: 1500, page: 1 }),
          apiService.get<CompanyFilterOption[]>(
            "/api/contacts/contact/employer-options/",
          ),
        ]);

        const sources = result.data.results
          .map((c) => c.contact_source)
          .filter((s): s is string => Boolean(s));
        setAllSources([...new Set(sources)]);

        const codes = result.data.results
          .map((c) => getPhoneCountryCode(c.phone ?? null))
          .filter((c): c is string => Boolean(c));
        setAvailablePhoneCountries([...new Set(codes)].sort());
        setCompanyFilterOptions(companiesResult.data || []);
      } catch (err) {
        console.error("Failed to fetch filter options:", err);
      }
    };

    fetchFilterOptions();
  }, []);

  const filteredContacts = useMemo(() => {
    const filtered = contacts.filter((contact) => {
      const matchesSearch =
        (contact.name &&
          contact.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.email &&
          contact.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.phone && contact.phone.includes(searchTerm)) ||
        (contact.address &&
          contact.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.first_name &&
          contact.first_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (contact.last_name &&
          contact.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        searchTerm === "";

      const matchesSource =
        sourceTab === "all" || contact.contact_source === sourceTab;
      const matchesPhoneCountry =
        filterPhoneCountry === "all" ||
        getPhoneCountryCode(contact.phone) === filterPhoneCountry;

      return matchesSearch && matchesSource && matchesPhoneCountry;
    });

    return filtered;
  }, [contacts, searchTerm, sourceTab, filterPhoneCountry]);

  // Drives the "select all" checkbox shown in the selection banner —
  // reflects whether every contact currently visible (on this page, under
  // the active filters/search) is selected. Selections on other pages are
  // unaffected either way.
  const allVisibleSelected = useMemo(
    () =>
      filteredContacts.length > 0 &&
      filteredContacts.every((contact) =>
        selectedContactIds.includes(contact.id),
      ),
    [filteredContacts, selectedContactIds],
  );

  const someVisibleSelected = useMemo(
    () =>
      filteredContacts.some((contact) =>
        selectedContactIds.includes(contact.id),
      ),
    [filteredContacts, selectedContactIds],
  );

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedContactsMap((prev) => {
      const next = { ...prev };
      if (allVisibleSelected) {
        filteredContacts.forEach((c) => {
          delete next[c.id];
        });
      } else {
        filteredContacts.forEach((c) => {
          next[c.id] = c;
        });
      }
      return next;
    });
  }, [allVisibleSelected, filteredContacts]);

  const selectAllFilteredContacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, string | number | undefined> = {
        page_size: 1000,
        search: searchTerm || undefined,
        contact_type: filterType === "all" ? undefined : filterType,
        contact_source: sourceTab === "all" ? undefined : sourceTab,
        company_id: filterCompanyId === "all" ? undefined : filterCompanyId,
      };
      const allMatches: ContactApiItem[] = [];
      let page = 1;
      let hasNextPage = true;
      while (hasNextPage) {
        const result = await apiService.get<{
          results: ContactApiItem[];
          next: string | null;
        }>("/api/contacts/contact/", { ...params, page });
        allMatches.push(...result.data.results);
        hasNextPage = Boolean(result.data.next);
        page += 1;
      }
      const matches = allMatches
        .filter((contact) =>
          filterPhoneCountry === "all"
            ? true
            : getPhoneCountryCode(contact.phone || null) === filterPhoneCountry,
        )
        .map(mapContactApiItem);
      setSelectedContactsMap(
        Object.fromEntries(matches.map((contact) => [contact.id, contact])),
      );
      toast.success(
        `${matches.length} filtered contact${matches.length === 1 ? "" : "s"} selected.`,
      );
    } catch {
      toast.error("Could not select the filtered contacts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, filterType, sourceTab, filterCompanyId, filterPhoneCountry]);

  const handleAddMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  // Selection mode works identically in grid and list view, and survives
  // search/page/filter changes — entering it doesn't force a view switch
  // or wipe anything.
  const enterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
    setExportMenuAnchor(null);
  }, []);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedContactsMap({});
    setExportMenuAnchor(null);
  }, []);

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleFilterMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const clearFilters = () => {
    setFilterType("all");
    setFilterPhoneCountry("all");
    setFilterCompanyId("all");
    setSourceTab("all");
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    handleFilterMenuClose();
  };

  const handleAddIndividual = () => {
    navigate("/contacts/add-individual");
    handleAddMenuClose();
  };

  const handleRetry = () => {
    fetchContacts();
  };

  const handleExportContact = useCallback((contact: IContact) => {
    downloadVCard([contact], vCardFilename(contact));
    try {
      toast.success("Exported vCard");
    } catch (e) {
      console.warn("Toast error:", e);
    }
  }, []);

  const handleExportContactCSV = useCallback((contact: IContact) => {
    const filename =
      (contact.name || contact.first_name || contact.last_name || "contact")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-|-$/g, "") || "contact";
    downloadCSV([contact], `${filename}.csv`);
    try {
      toast.success("Exported CSV");
    } catch (e) {
      console.warn("Toast error:", e);
    }
  }, []);

  const handleExportSelectedContacts = useCallback(
    (format: "vcf" | "csv") => {
      if (selectedContacts.length === 0) {
        toast.error("Select contacts before exporting.");
        return;
      }

      setIsExporting(true);

      try {
        const filename = `muzukuru-selected-contacts-${new Date()
          .toISOString()
          .slice(0, 10)}`;

        if (format === "csv") {
          downloadCSV(selectedContacts, `${filename}.csv`);
        } else {
          downloadVCard(selectedContacts, `${filename}.vcf`);
        }

        toast.success(
          `${selectedContacts.length} contact${
            selectedContacts.length === 1 ? "" : "s"
          } exported ${format === "csv" ? "as CSV" : "as vCard"}`,
        );

        // Back to a clean slate: cards go unticked, selection mode turns
        // off, and the button reverts to "Select to export".
        exitSelectionMode();
      } catch (e) {
        console.warn("Toast error:", e);
      } finally {
        setIsExporting(false);
      }
    },
    [selectedContacts, exitSelectionMode],
  );

  const handleViewContact = useCallback(
    (id: number, type: "company" | "individual") => {
      navigate(
        type === "company"
          ? `/contacts/company/view/${id}`
          : `/contacts/individual/view/${id}`,
      );
    },
    [navigate],
  );

  const handleEditContact = useCallback(
    (id: number, type: "company" | "individual") => {
      navigate(
        type === "company"
          ? `/contacts/company/edit/${id}`
          : `/contacts/individual/edit/${id}`,
      );
    },
    [navigate],
  );

  const handleDeleteClick = (id: number, name: string) => {
    setContactToDeleteId(id);
    setContactToDeleteName(name);
    setOpenDeleteModal(true);
  };
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setContactToDeleteId(null);
    setContactToDeleteName(null);
  };

  const handleConfirmDelete = async () => {
    if (contactToDeleteId) {
      try {
        await apiService.delete(`/api/contacts/contact/${contactToDeleteId}/`);
        fetchContacts();
        handleCloseDeleteModal();
      } catch (err) {
        setError("Failed to delete contact. Please try again.");
        console.error("Delete error:", err);
        handleCloseDeleteModal();
      }
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const accent = accentForContactType(params.row.contact_type);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <Avatar
                src={params.row.contact_image}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: accent.bg,
                  color: accent.fg,
                  border: `1px solid ${accent.border}`,
                  fontWeight: 700,
                }}
              >
                {params.row.contact_type === "company" ? (
                  <BusinessIcon fontSize="small" />
                ) : (
                  `${params.row.first_name?.[0] || ""}`.toUpperCase() || (
                    <PersonIcon fontSize="small" />
                  )
                )}
              </Avatar>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                <Typography variant="body2" fontWeight={600}>
                  {params.row.contact_type === "company"
                    ? params.row.name || "Unnamed Company"
                    : `${params.row.first_name || ""} ${
                        params.row.last_name || ""
                      }`.trim() || "Unnamed Individual"}
                </Typography>
                <Chip
                  label={
                    params.row.contact_type === "company"
                      ? "Company"
                      : "Individual"
                  }
                  size="small"
                  sx={{
                    height: 18,
                    width: "fit-content",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: accent.bg,
                    color: accent.fg,
                    border: `1px solid ${accent.border}`,
                    ".MuiChip-label": { px: 0.8 },
                  }}
                />
              </Box>
            </Box>
          );
        },
      },

      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 0.8,
        minWidth: 140,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "address",
        headerName: "Address",
        flex: 1,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "normal", wordWrap: "break-word", mt: 1.5 }}
          >
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        width: 150,
        renderCell: (params) => (
          <Box>
            <Tooltip title="Edit">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation(); // 👈 prevent row click
                  handleEditContact(params.row.id, params.row.contact_type);
                }}
                sx={{
                  color: reduxTheme.primary_colors?.[400] || "#614438",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                  },
                }}
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteClick(
                    params.row.id,
                    params.row.contact_type === "company"
                      ? params.row.name || "Unnamed Company"
                      : `${params.row.first_name || ""} ${
                          params.row.last_name || ""
                        }`.trim() || "Unnamed Individual",
                  );
                }}
                sx={{
                  color: "#EB4335",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                  },
                }}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>

            <Tooltip title="Export vCard">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleExportContact(params.row);
                }}
                sx={{
                  color: reduxTheme.primary_colors?.[400] || "#614438",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                  },
                }}
              >
                <FileDownloadOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [reduxTheme, handleEditContact, handleExportContact],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Box
              sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}
            >
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  maxWidth:
                    searchTerm && filteredContacts.length > 0 ? 700 : 400,
                  transition: "max-width 0.3s ease",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "background.paper",
                    border: `1.5px solid ${primaryColor}33`,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:focus-within": {
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 3px ${primaryColor}22`,
                    },
                  }}
                >
                  <SearchOutlined sx={{ mr: 1, color: primaryColor }} />
                  <InputBase
                    placeholder="Search by name, email, phone, or address..."
                    sx={{ ml: 1, flex: 1, color: "text.primary" }}
                    onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                  />
                  <IconButton onClick={handleFilterMenuClick}>
                    <FilterListOutlined sx={{ color: primaryColor }} />
                  </IconButton>
                  <Drawer
                    anchor="right"
                    open={Boolean(filterMenuAnchor)}
                    onClose={handleFilterMenuClose}
                    PaperProps={{
                      sx: {
                        width: { xs: "100%", sm: 400 },
                        bgcolor: "background.default",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          p: 3,
                          pb: 2.5,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: `${primaryColor}1A`,
                            flexShrink: 0,
                          }}
                        >
                          <FilterListOutlined
                            sx={{ color: primaryColor, fontSize: 20 }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ lineHeight: 1.3 }}
                          >
                            {isSelectionMode
                              ? "Refine your export"
                              : "Filter contacts"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.25 }}
                          >
                            {isSelectionMode
                              ? "Narrow the list, then select the contacts to include."
                              : "Narrow the list by the details that matter."}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={handleFilterMenuClose}
                          aria-label="Close filters"
                          size="small"
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              bgcolor: `${primaryColor}14`,
                              color: primaryColor,
                            },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                      <Divider />

                      {/* Active filter summary */}
                      {activeFilterCount > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            px: 3,
                            py: 1.75,
                            bgcolor: "background.paper",
                          }}
                        >
                          {filterType !== "all" && (
                            <Chip
                              size="small"
                              label={
                                filterType === "company"
                                  ? "Company contacts"
                                  : "Individuals"
                              }
                              onDelete={() => setFilterType("all")}
                              sx={{
                                bgcolor: `${primaryColor}14`,
                                color: primaryTextColor,
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {filterCompanyId !== "all" && (
                            <Chip
                              size="small"
                              label={
                                companyFilterOptions.find(
                                  (c) => String(c.id) === filterCompanyId,
                                )?.name || "Company"
                              }
                              onDelete={() => setFilterCompanyId("all")}
                              sx={{
                                bgcolor: `${primaryColor}14`,
                                color: primaryTextColor,
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {filterPhoneCountry !== "all" && (
                            <Chip
                              size="small"
                              label={getCountryDisplayName(filterPhoneCountry)}
                              onDelete={() => setFilterPhoneCountry("all")}
                              sx={{
                                bgcolor: `${primaryColor}14`,
                                color: primaryTextColor,
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {sourceTab !== "all" && (
                            <Chip
                              size="small"
                              label={sourceTab.replace(/_/g, " ")}
                              onDelete={() => setSourceTab("all")}
                              sx={{
                                bgcolor: `${primaryColor}14`,
                                color: primaryTextColor,
                                fontWeight: 600,
                                textTransform: "capitalize",
                              }}
                            />
                          )}
                        </Box>
                      )}
                      <Divider />

                      {/* Filter sections */}
                      <Box
                        sx={{
                          flex: 1,
                          overflowY: "auto",
                          p: 3,
                          display: "flex",
                          flexDirection: "column",
                          gap: 3.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <PersonIcon
                              sx={{ fontSize: 15, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                letterSpacing: 0.4,
                                color: "text.secondary",
                                fontWeight: 600,
                              }}
                            >
                              Who
                            </Typography>
                          </Box>
                          <FormControl fullWidth size="small">
                            <InputLabel id="drawer-contact-type">
                              Contact type
                            </InputLabel>
                            <Select
                              labelId="drawer-contact-type"
                              label="Contact type"
                              value={filterType}
                              onChange={(e) =>
                                setFilterType(
                                  e.target.value as
                                    | "all"
                                    | "company"
                                    | "individual",
                                )
                              }
                              sx={{ borderRadius: "8px" }}
                            >
                              <MenuItem value="all">All contact types</MenuItem>
                              <MenuItem value="individual">
                                Individuals
                              </MenuItem>
                              <MenuItem value="company">
                                Company contacts
                              </MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl fullWidth size="small">
                            <InputLabel id="drawer-company">
                              Works at company
                            </InputLabel>
                            <Select
                              labelId="drawer-company"
                              label="Works at company"
                              value={filterCompanyId}
                              onChange={(e) =>
                                setFilterCompanyId(e.target.value)
                              }
                              sx={{ borderRadius: "8px" }}
                            >
                              <MenuItem value="all">Any company</MenuItem>
                              {companyFilterOptions.map((company) => (
                                <MenuItem
                                  key={company.id}
                                  value={String(company.id)}
                                >
                                  {company.name}
                                  {typeof company.contact_count === "number"
                                    ? ` (${company.contact_count})`
                                    : ""}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        <Divider
                          sx={{
                            borderColor:
                              reduxTheme.neutral_colors?.[800] || "#f0f0f0",
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <CallIcon
                              sx={{ fontSize: 15, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                letterSpacing: 0.4,
                                color: "text.secondary",
                                fontWeight: 600,
                              }}
                            >
                              Reachability
                            </Typography>
                          </Box>
                          <FormControl fullWidth size="small">
                            <InputLabel id="drawer-phone-country">
                              Phone country
                            </InputLabel>
                            <Select
                              labelId="drawer-phone-country"
                              label="Phone country"
                              value={filterPhoneCountry}
                              onChange={(e) =>
                                setFilterPhoneCountry(e.target.value)
                              }
                              sx={{ borderRadius: "8px" }}
                            >
                              <MenuItem value="all">Any phone country</MenuItem>
                              {availablePhoneCountries.map((code) => (
                                <MenuItem key={code} value={code}>
                                  {getCountryDisplayName(code)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        <Divider
                          sx={{
                            borderColor:
                              reduxTheme.neutral_colors?.[800] || "#f0f0f0",
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <BusinessIcon
                              sx={{ fontSize: 15, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                letterSpacing: 0.4,
                                color: "text.secondary",
                                fontWeight: 600,
                              }}
                            >
                              Origin
                            </Typography>
                          </Box>
                          <FormControl fullWidth size="small">
                            <InputLabel id="drawer-contact-source">
                              Contact source
                            </InputLabel>
                            <Select
                              labelId="drawer-contact-source"
                              label="Contact source"
                              value={sourceTab}
                              onChange={(e) => setSourceTab(e.target.value)}
                              sx={{ borderRadius: "8px" }}
                            >
                              {contactSourceTabs.map((source) => (
                                <MenuItem
                                  key={source}
                                  value={source}
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {source === "all"
                                    ? "Any source"
                                    : source.replace(/_/g, " ")}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      {/* Footer */}
                      <Box
                        sx={{
                          p: 2.5,
                          bgcolor: "background.paper",
                          borderTop: `1px solid ${reduxTheme.primary_colors?.[800] || "#f0f0f0"}`,
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Button
                          fullWidth
                          onClick={clearFilters}
                          disabled={activeFilterCount === 0}
                          sx={{ color: "text.secondary", borderRadius: "8px" }}
                        >
                          {activeFilterCount > 0
                            ? `Clear all (${activeFilterCount})`
                            : "Clear all"}
                        </Button>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={applyFilters}
                          sx={{
                            bgcolor: primaryColor,
                            borderRadius: "8px",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: primaryHoverColor,
                              boxShadow: "none",
                            },
                          }}
                        >
                          Apply filters
                        </Button>
                      </Box>
                    </Box>
                  </Drawer>
                </Box>

                {/* Dropdown search results. When selection mode is active,
                    rows show a checkbox and clicking toggles selection
                    instead of navigating away — this is how a user adds
                    more contacts to an export batch by searching. */}
                {!isLoading && searchTerm && filteredContacts.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      mt: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: "none",
                        maxHeight: 400,
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: "4px" },
                        "&::-webkit-scrollbar-track": {
                          background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: `${primaryColor}40`,
                          borderRadius: "4px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          borderBottom: `1px solid ${reduxTheme.neutral_colors?.[800] || "#eee"}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: secondaryTextColor,
                            opacity: 0.6,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {isSelectionMode
                            ? "Tap to add to selection"
                            : "Contacts"}
                        </Typography>
                      </Box>

                      {filteredContacts.map((contact) => {
                        const isRowSelected = selectedContactIds.includes(
                          contact.id,
                        );
                        return (
                          <Box
                            key={contact.id}
                            onClick={() => {
                              if (isSelectionMode) {
                                toggleContactSelection(contact);
                              } else {
                                handleViewContact(
                                  contact.id,
                                  contact.contact_type,
                                );
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2,
                              py: 1.5,
                              cursor: "pointer",
                              borderBottom: `1px solid ${reduxTheme.neutral_colors?.[800] || "#eee"}`,
                              bgcolor: isRowSelected
                                ? `${primaryColor}14`
                                : "transparent",
                              "&:hover": { backgroundColor: backgroundColor },
                            }}
                          >
                            {isSelectionMode && (
                              <Checkbox
                                checked={isRowSelected}
                                onClick={(event) => event.stopPropagation()}
                                onChange={() => toggleContactSelection(contact)}
                                sx={{
                                  p: 0.5,
                                  color: primaryColor,
                                  "&.Mui-checked": { color: primaryColor },
                                }}
                              />
                            )}
                            <Avatar
                              src={contact.contact_image || undefined}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: primaryColor,
                              }}
                            >
                              {contact.contact_type === "company" ? (
                                <BusinessIcon />
                              ) : (
                                `${contact.first_name?.[0] || ""}${contact.last_name?.[0] || ""}`
                              )}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ color: primaryTextColor }}
                              >
                                {getContactDisplayName(contact)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: secondaryTextColor, opacity: 0.7 }}
                                noWrap
                              >
                                {contact.email ||
                                  contact.phone ||
                                  "No contact info"}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: secondaryTextColor,
                                opacity: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              {contact.contact_type}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>{" "}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListOutlined />}
                  onClick={handleFilterMenuClick}
                  sx={{
                    borderColor: primaryColor,
                    color: primaryColor,
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeFilterCount
                    ? `Filters (${activeFilterCount})`
                    : "Filter contacts"}
                </Button>
                <IconButton
                  onClick={() => setViewMode("grid")}
                  sx={{
                    color:
                      viewMode === "grid" ? primaryColor : secondaryTextColor,
                  }}
                >
                  <GridViewIcon />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode("list")}
                  sx={{
                    color:
                      viewMode === "list" ? primaryColor : secondaryTextColor,
                  }}
                >
                  <ViewListIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip
                title={
                  isSelectionMode
                    ? "Tick contacts, then export them"
                    : "Pick contacts to build an export"
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadOutlined />}
                    onClick={(event) => {
                      if (!isSelectionMode) {
                        enterSelectionMode();
                        handleFilterMenuClick(event);
                        return;
                      }

                      if (selectedContactIds.length === 0) {
                        toast.info("Select contacts first.");
                        return;
                      }

                      handleExportMenuClick(event);
                    }}
                    disabled={isLoading || isExporting}
                    sx={{
                      borderColor: primaryColor,
                      color: primaryColor,
                      "&:hover": {
                        borderColor: primaryHoverColor,
                        color: primaryHoverColor,
                      },
                    }}
                  >
                    {isExporting
                      ? "Exporting..."
                      : isSelectionMode
                        ? `Export${selectedContactIds.length > 0 ? ` (${selectedContactIds.length})` : ""}`
                        : "Select to export"}
                  </Button>
                </span>
              </Tooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
                PaperProps={{
                  sx: {
                    boxShadow: 0,
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleExportSelectedContacts("vcf");
                    handleExportMenuClose();
                  }}
                >
                  Export selected vCard
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleExportSelectedContacts("csv");
                    handleExportMenuClose();
                  }}
                >
                  Export selected CSV
                </MenuItem>
              </Menu>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddMenuClick}
                sx={{
                  bgcolor: primaryColor,
                  "&:hover": { bgcolor: primaryHoverColor },
                  color: "#fff",
                }}
              >
                Add Contact
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={handleAddMenuClose}
              >
                <MenuItem onClick={handleAddIndividual}>
                  <PersonIcon sx={{ mr: 1, color: primaryTextColor }} />
                  Add Individual Contact
                </MenuItem>
                {/* <MenuItem onClick={handleAddCompany}>
                  <BusinessIcon sx={{ mr: 1, color: primaryTextColor }} />
                  Add Company Contact
                </MenuItem> */}
              </Menu>
            </Box>
          </Box>
        </motion.div>

        {warning && !searchTerm && (
          <Alert
            severity="warning"
            sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
          >
            {warning}
          </Alert>
        )}

        {/* Selection banner — shared between grid and list view, sticky so
            "Export Selected" stays reachable while scrolling or browsing
            through pages/search results to build up a selection. */}
        {isSelectionMode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
              p: 2,
              borderRadius: "12px",
              bgcolor: "background.paper",
              border: `1px solid ${reduxTheme.neutral_colors?.[800] || "#eee"}`,
              position: "sticky",
              top: 0,
              zIndex: 20,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                onChange={toggleSelectAllVisible}
                disabled={filteredContacts.length === 0}
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": { color: primaryColor },
                  "&.MuiCheckbox-indeterminate": { color: primaryColor },
                }}
              />
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    selectedContactIds.length > 0
                      ? `${primaryColor}1A`
                      : reduxTheme.neutral_colors?.[900] || "#f2f2f2",
                  flexShrink: 0,
                }}
              >
                <FileDownloadOutlined
                  sx={{
                    fontSize: 18,
                    color:
                      selectedContactIds.length > 0
                        ? primaryColor
                        : secondaryTextColor,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "text.primary",
                    fontWeight: 700,
                    lineHeight: 1.3,
                  }}
                >
                  {selectedContactIds.length === 0
                    ? "Choose contacts to export"
                    : `${selectedContactIds.length} contact${selectedContactIds.length === 1 ? "" : "s"} selected`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totalItems} contact{totalItems === 1 ? "" : "s"} match the
                  current filters
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={selectAllFilteredContacts}
                disabled={isLoading || totalItems === 0}
                sx={{
                  color: primaryColor,
                  textTransform: "none",
                  fontWeight: 600,
                  ml: 1,
                }}
              >
                Select all matching
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={exitSelectionMode}
                sx={{ color: "text.secondary", textTransform: "none" }}
              >
                Cancel
              </Button>
              <Tooltip
                title={
                  selectedContactIds.length === 0
                    ? "Select at least one contact"
                    : ""
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
                    disabled={selectedContactIds.length === 0}
                    onClick={handleExportMenuClick}
                    sx={{
                      bgcolor: primaryColor,
                      color: "#fff",
                      borderRadius: "8px",
                      textTransform: "none",
                      "&:hover": { bgcolor: primaryHoverColor },
                      "&.Mui-disabled": {
                        bgcolor: reduxTheme.neutral_colors?.[900] || "#eee",
                        color: secondaryTextColor,
                      },
                    }}
                  >
                    Export
                    {selectedContactIds.length > 0
                      ? ` (${selectedContactIds.length})`
                      : ""}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tabs
            value={sourceTab}
            onChange={(_, newValue) => {
              setSourceTab(newValue);
              setCurrentPage(1);
            }}
            sx={{
              mb: 2,
              ".MuiTab-root": {
                color: "text.secondary",
                textTransform: "capitalize",
                fontWeight: 600,
              },
              ".Mui-selected": { color: primaryColor },
            }}
            TabIndicatorProps={{ style: { backgroundColor: primaryColor } }}
          >
            {contactSourceTabs.map((source) => (
              <Tab
                key={source}
                label={source === "all" ? "All" : source.replace(/_/g, " ")}
                value={source}
              />
            ))}
          </Tabs>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              size="small"
              startIcon={<FilterListOutlined />}
              onClick={(e) => setSortAnchorEl(e.currentTarget)}
              sx={{
                color: primaryColor,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Sort:{" "}
              {sortField === "created_at"
                ? "Date created"
                : sortField[0].toUpperCase() + sortField.slice(1)}
            </Button>
            <Tooltip
              title={`Sort ${sortOrder === "asc" ? "descending" : "ascending"}`}
            >
              <IconButton
                onClick={() => {
                  setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                  setCurrentPage(1);
                }}
              >
                {sortOrder === "asc" ? (
                  <ArrowUpward sx={{ color: primaryColor }} />
                ) : (
                  <ArrowDownward sx={{ color: primaryColor }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={() => setSortAnchorEl(null)}
          >
            <MenuItem
              selected={sortField === "name"}
              onClick={() => {
                setSortField("name");
                setCurrentPage(1);
                setSortAnchorEl(null);
              }}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              Name
              {sortField === "name" && <Check fontSize="small" />}
            </MenuItem>

            <MenuItem
              selected={sortField === "email"}
              onClick={() => {
                setSortField("email");
                setCurrentPage(1);
                setSortAnchorEl(null);
              }}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              Email
              {sortField === "email" && <Check fontSize="small" />}
            </MenuItem>

            <MenuItem
              selected={sortField === "created_at"}
              onClick={() => {
                setSortField("created_at");
                setCurrentPage(1);
                setSortAnchorEl(null);
              }}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              Date Created
              {sortField === "created_at" && <Check fontSize="small" />}
            </MenuItem>
          </Menu>
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress sx={{ color: primaryColor }} />
          </Box>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!isLoading && !error && filteredContacts.length === 0 && (
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", textAlign: "center", my: 4 }}
          >
            No contacts found.
          </Typography>
        )}

        {!isLoading &&
          !error &&
          filteredContacts.length > 0 &&
          !searchTerm &&
          viewMode === "grid" && (
            <Grid container spacing={2}>
              {filteredContacts.map((contact) => {
                const cardAccent = accentForContactType(contact.contact_type);
                const isSelected = selectedContactIds.includes(contact.id);
                return (
                  <Grid size={{ xs: 12, md: 3 }} key={contact.id}>
                    <Card
                      onClick={() => {
                        if (isSelectionMode) {
                          toggleContactSelection(contact);
                        } else {
                          enterSelectionMode();
                          toggleContactSelection(contact);
                        }
                      }}
                      sx={{
                        borderRadius: 3,
                        boxShadow: isSelected
                          ? `0 0 0 3px ${primaryColor}33`
                          : "0 1px 3px rgba(0,0,0,0.06)",
                        p: 2,
                        pt: 2.5,
                        minHeight: 300,
                        bgcolor: "background.paper",
                        borderTop: `4px solid ${cardAccent.solid}`,
                        border: isSelected
                          ? `1.5px solid ${primaryColor}`
                          : `1px solid ${cardAccent.border}`,
                        borderTopWidth: 4,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        position: "relative",
                        cursor: isSelectionMode ? "pointer" : "default",
                        transition:
                          "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          transform: isSelectionMode
                            ? "none"
                            : "translateY(-4px)",
                          boxShadow: isSelected
                            ? `0 0 0 3px ${primaryColor}33`
                            : `0 10px 24px ${cardAccent.border}`,
                        },
                        "&:hover .menu-button": {
                          opacity: 1,
                        },
                      }}
                    >
                      {/* Completeness Score */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                        }}
                      >
                        <CompletenessScore
                          score={contact.completeness_score}
                          size={55}
                        />
                      </Box>

                      {isSelectionMode ? (
                        <Checkbox
                          checked={isSelected}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => toggleContactSelection(contact)}
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            zIndex: 1,
                            color: cardAccent.fg,
                            "&.Mui-checked": { color: primaryColor },
                          }}
                        />
                      ) : (
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();

                            setSelectedContact({
                              id: contact.id,
                              name:
                                contact.contact_type === "company"
                                  ? contact.name || "Unnamed Company"
                                  : `${contact.first_name || ""} ${
                                      contact.last_name || ""
                                    }`.trim() ||
                                    contact.name ||
                                    "Unnamed Individual",
                            });
                            handleMenuClick(event);
                          }}
                          className="menu-button"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            opacity: 0,
                            transition: "opacity 0.2s ease-in-out",
                            zIndex: 1,
                            bgcolor: "background.paper",
                            border: `1px solid ${cardAccent.border}`,
                            "&:hover": {
                              bgcolor: cardAccent.bg,
                            },
                          }}
                        >
                          <MoreVert sx={{ color: cardAccent.fg }} />
                        </IconButton>
                      )}

                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        PaperProps={{
                          sx: {
                            boxShadow: 0,
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleEditContact(
                              selectedContact.id!,
                              contact.contact_type,
                            );
                            handleMenuClose();
                          }}
                        >
                          <Edit fontSize="small" style={{ marginRight: 1 }} />
                          Edit
                        </MenuItem>

                        <MenuItem
                          onClick={() => {
                            if (selectedContact.id) {
                              const toExport = contacts.find(
                                (c) => c.id === selectedContact.id,
                              );
                              if (toExport) {
                                handleExportContact(toExport);
                              } else {
                                toast.error("Contact not found for export");
                              }
                            }
                            handleMenuClose();
                          }}
                        >
                          <FileDownloadOutlined
                            fontSize="small"
                            style={{ marginRight: 1 }}
                          />
                          Export vCard
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            if (selectedContact.id) {
                              const toExport = contacts.find(
                                (c) => c.id === selectedContact.id,
                              );
                              if (toExport) {
                                handleExportContactCSV(toExport);
                              } else {
                                toast.error("Contact not found for export");
                              }
                            }
                            handleMenuClose();
                          }}
                        >
                          <FileDownloadOutlined
                            fontSize="small"
                            style={{ marginRight: 1 }}
                          />
                          Export CSV
                        </MenuItem>

                        <MenuItem
                          onClick={() => {
                            if (selectedContact.id) {
                              setContactToDeleteId(selectedContact.id);
                              setContactToDeleteName(selectedContact.name);
                              setOpenDeleteModal(true);
                            }
                            handleMenuClose();
                          }}
                        >
                          <Delete fontSize="small" style={{ marginRight: 1 }} />
                          Delete
                        </MenuItem>
                      </Menu>

                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          pb: 1,
                        }}
                      >
                        <Avatar
                          src={contact.contact_image || undefined}
                          sx={{
                            width: 80,
                            height: 80,
                            mb: 2,
                            bgcolor: cardAccent.bg,
                            color: cardAccent.fg,
                            border: `2px solid ${cardAccent.border}`,
                            fontWeight: 700,
                            fontSize: "1.6rem",
                          }}
                        >
                          {contact.contact_type === "company"
                            ? contact.contact_image || (
                                <BusinessIcon fontSize="large" />
                              )
                            : `${contact.first_name?.[0] || ""}${
                                contact.last_name?.[0] || ""
                              }` || <PersonIcon fontSize="large" />}
                        </Avatar>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          align="center"
                          sx={{ color: primaryTextColor, mb: 0.5 }}
                        >
                          {getContactDisplayName(contact)}
                        </Typography>
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ mb: 0.5, color: "text.secondary" }}
                        >
                          {contact.email || "-"}
                        </Typography>
                        <Chip
                          label={
                            contact.contact_type === "company"
                              ? contact.industry || "Company"
                              : "Individual"
                          }
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 700,
                            textTransform: "capitalize",
                            bgcolor: cardAccent.bg,
                            color: cardAccent.fg,
                            border: `1px solid ${cardAccent.border}`,
                          }}
                        />
                      </CardContent>

                      <CardActions
                        sx={{
                          justifyContent: "center",
                          pt: 0,
                          flexDirection: "column",
                          gap: 1,
                          // In selection mode, dim the per-contact actions so
                          // it's visually clear the card's primary role right
                          // now is picking, not acting — but keep them
                          // clickable in case the user wants to jump into a
                          // single contact without leaving selection mode.
                          opacity: isSelectionMode ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Contact">
                            <Button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleViewContact(
                                  contact.id,
                                  contact.contact_type,
                                );
                              }}
                              sx={{
                                color: reduxTheme.primary_colors[900],
                                backgroundColor: primaryColor,
                              }}
                            >
                              View Contact
                            </Button>
                          </Tooltip>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={contact.phone || "No phone number"}>
                            <IconButton
                              disabled={!contact.phone}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (contact.phone) {
                                  window.open(`tel:${contact.phone}`);
                                }
                              }}
                              sx={{
                                color: contact.phone
                                  ? primaryColor
                                  : secondaryTextColor,
                                opacity: contact.phone ? 1 : 0.5,
                                "&:hover": {
                                  color: contact.phone
                                    ? primaryHoverColor
                                    : secondaryTextColor,
                                },
                              }}
                            >
                              <CallIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              contact.phone
                                ? "Message on WhatsApp"
                                : "No phone number"
                            }
                          >
                            <span>
                              <IconButton
                                disabled={!contact.phone}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (contact.phone) {
                                    window.open(
                                      toWhatsAppLink(contact.phone),
                                      "_blank",
                                    );
                                  }
                                }}
                                sx={{
                                  color: contact.phone
                                    ? "#25D366"
                                    : secondaryTextColor,
                                  opacity: contact.phone ? 1 : 0.5,
                                  "&:hover": {
                                    color: contact.phone
                                      ? "#1DA851"
                                      : secondaryTextColor,
                                  },
                                }}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={contact.website || "No website"}>
                            <IconButton
                              disabled={!contact.website}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (contact.website) {
                                  window.open(contact.website, "_blank");
                                }
                              }}
                              sx={{
                                color: contact.website
                                  ? primaryColor
                                  : secondaryTextColor,
                                opacity: contact.website ? 1 : 0.5,
                                "&:hover": {
                                  color: contact.website
                                    ? primaryHoverColor
                                    : secondaryTextColor,
                                },
                              }}
                            >
                              <LanguageIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={contact.email || "No email"}>
                            <IconButton
                              disabled={!contact.email}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (contact.email) {
                                  window.open(`mailto:${contact.email}`);
                                }
                              }}
                              sx={{
                                color: contact.email
                                  ? primaryColor
                                  : secondaryTextColor,
                                opacity: contact.email ? 1 : 0.5,
                                "&:hover": {
                                  color: contact.email
                                    ? primaryHoverColor
                                    : secondaryTextColor,
                                },
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Export vCard">
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleExportContact(contact);
                              }}
                              sx={{
                                color: primaryColor,
                                "&:hover": { color: primaryHoverColor },
                              }}
                            >
                              <FileDownloadOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

        {!isLoading &&
          !error &&
          filteredContacts.length > 0 &&
          !searchTerm &&
          viewMode === "list" && (
            <Box>
              <DataGrid
                rows={filteredContacts}
                columns={columns}
                getRowId={(row) => row.id}
                checkboxSelection={isSelectionMode}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                disableRowSelectionOnClick
                navigateToOnRowClick={(id) => `/contacts/individual/view/${id}`}
                loading={isLoading}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={{ page: currentPage - 1, pageSize }}
                onPaginationModelChange={(model) => {
                  setCurrentPage(model.page);
                  setPageSize(model.pageSize);
                }}
              />
            </Box>
          )}
        {!isLoading && filteredContacts.length > 0 && viewMode === "grid" && (
          <Pagination
            currentPage={currentPage - 1}
            pageSize={pageSize}
            totalCount={totalItems}
            onPageChange={(page) => setCurrentPage(page + 1)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        )}
      </Box>

      <Dialog
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            bgcolor: backgroundColor,
            color: primaryTextColor,
          },
        }}
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            sx={{ color: secondaryTextColor }}
          >
            Are you sure you want to delete the contact:{" "}
            <Typography
              component="span"
              fontWeight="bold"
              sx={{ color: primaryTextColor }}
            >
              {contactToDeleteName}
            </Typography>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteModal}
            sx={{
              color: primaryColor,
              "&:hover": { bgcolor: primaryColor, color: "#fff" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: "error.main",
              "&:hover": { bgcolor: "error.dark" },
              color: "#fff",
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default ListContacts;
